import { useCallback, useRef, useState } from 'react';

/**
 * Hook that detects whether a text element is being truncated by CSS
 * (e.g., via `text-overflow: ellipsis`).
 *
 * Returns a callback ref to attach to the element and a boolean indicating
 * whether the element's content is currently truncated.
 */
export const useIsTruncated = (): [ref: (node: HTMLElement | null) => void, isTruncated: boolean] => {
  const [isTruncated, setIsTruncated] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (node) {
      const check = () => {
        // Use requestAnimationFrame to ensure the DOM has updated with the new value and avoid the noisy but benign ResizeObserver loop errors.
        requestAnimationFrame(() => {
          setIsTruncated(node.scrollWidth > node.clientWidth);
        });
      };
      check();

      observerRef.current = new ResizeObserver(check);
      observerRef.current.observe(node);
    }
  }, []);

  return [ref, isTruncated];
};
